import type { OperationId, Session } from '@hlt/contracts';
import type { QueryClient } from '@tanstack/react-query';
import { apiKey, apiQueryOptions, type ApiQueryOptions } from '@hlt/web-kit/api';
import type { WorkspaceRouteScope } from '@/app/route-ids';
import { toListQuery, type AuditListSearch } from '@/features/audits/list/audit-search';
import { templateVersionsQuery } from '@/features/audits/shared/use-workspace-data';
import { toFindingQuery, type FindingListSearch } from '@/features/findings/list/finding-search';

/**
 * Route loaders start a page's main read. The router runs them when a link is
 * pointed at, focused or touched (`defaultPreload: 'intent'`), so the request is under way before the click
 * and the page usually opens with its data. Loaders never wait: the page renders at once and shows its
 * skeleton if the read is still running. Keys and inputs match what the pages read; fresh data (30 s) is
 * not requested again.
 */
function start<Op extends OperationId>(queryClient: QueryClient, options: ApiQueryOptions<Op>): void {
    void queryClient.prefetchQuery(options).catch(() => undefined);
}

const userIdOf = (queryClient: QueryClient): string | null =>
    queryClient.getQueryData<Session>(apiKey('getSession'))?.userId ?? null;

export const prefetch = {
    /**
     * What every workspace page reads besides its own list, started together with it instead of one after
     * the other: the workspace, the tool controls, the template list (setup check), people and versions.
     */
    workspace(queryClient: QueryClient, scope: WorkspaceRouteScope): void {
        const params = { ...scope };
        start(queryClient, apiQueryOptions('getWorkspace', { params }));
        start(queryClient, apiQueryOptions('getToolAccess', { params: { organizationId: scope.organizationId } }));
        start(queryClient, apiQueryOptions('listTemplates', { params, input: { limit: 100 } }));
        start(queryClient, apiQueryOptions('listWorkspacePeople', { params, input: { limit: 100 } }));
        void queryClient.prefetchQuery(templateVersionsQuery(scope)).catch(() => undefined);
    },
    home(queryClient: QueryClient, scope: WorkspaceRouteScope): void {
        start(
            queryClient,
            apiQueryOptions('listAudits', {
                params: { ...scope },
                input: { status: 'submitted', limit: 5, sort: 'effectiveAt' },
            }),
        );
    },
    audits(queryClient: QueryClient, scope: WorkspaceRouteScope, search: AuditListSearch): void {
        start(queryClient, apiQueryOptions('listAudits', { params: { ...scope }, input: toListQuery(search) }));
    },
    findings(queryClient: QueryClient, scope: WorkspaceRouteScope, search: FindingListSearch): void {
        const userId = userIdOf(queryClient);
        if (search.owner === 'me' && !userId) return;
        start(
            queryClient,
            apiQueryOptions('listFindings', { params: { ...scope }, input: toFindingQuery(search, userId) }),
        );
    },
    templates(queryClient: QueryClient, scope: WorkspaceRouteScope): void {
        start(
            queryClient,
            apiQueryOptions('listTemplates', { params: { ...scope }, input: { limit: 100, sort: 'name' } }),
        );
    },
    dashboards(queryClient: QueryClient, scope: WorkspaceRouteScope): void {
        start(queryClient, apiQueryOptions('listPages', { params: { ...scope }, input: { limit: 100 } }));
    },
    audit(queryClient: QueryClient, scope: WorkspaceRouteScope, auditId: string): void {
        start(queryClient, apiQueryOptions('getAudit', { params: { ...scope, auditId } }));
    },
    finding(queryClient: QueryClient, scope: WorkspaceRouteScope, findingId: string): void {
        start(queryClient, apiQueryOptions('getFinding', { params: { ...scope, findingId } }));
    },
    template(queryClient: QueryClient, scope: WorkspaceRouteScope, templateId: string): void {
        start(queryClient, apiQueryOptions('getTemplate', { params: { ...scope, templateId } }));
    },
};
